import { getLatestGeoLocation, ParsedGeoLocation } from "./geo-location";

export const PUSH = true;
export const REPLACE = false;

export type UpdateType = typeof PUSH|typeof REPLACE;

interface UpdateArgs {
  type: UpdateType,
  url?: string,
  scroll?: number,
}

export interface HistoryState {
  // The value of Date.now() when this was saved.
  time: number;
  // The last scroll position. Used on page-reload.
  scroll: number;
  // A count of the nubmer of times we've pushed to the stack.
  stackSize: number;
  location?: ParsedGeoLocation;
}

export class HistoryManager {
  private scrollSaveTimer = 0;
  private scrollSavingEnabled = false;

  constructor() {
    document.addEventListener('scroll', this.handleScrollEvent.bind(this));
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    if (this.state) {
      window.scrollTo(0, this.state.scroll);
    }
  }

  disableScrollSaving() {
    clearTimeout(this.scrollSaveTimer);
    this.scrollSavingEnabled = false;
  }

  enableScrollSaving() {
    this.scrollSavingEnabled = true;
    this.saveCurrentScroll();
  }

  updateState() {
    this.update({type: REPLACE});
  }

  update(args: UpdateArgs) {
    if (args.type === PUSH) {
      // Before we leave this spot in the stack, make sure the saved scroll position is correct.
      this.saveCurrentScroll();
    }
    let stackSize = this.state?.stackSize ?? 0;
    if (args.type === PUSH) {
      stackSize++;
    }
    const newState: HistoryState = {
      time: Date.now(),
      scroll: args.scroll ?? (args.type === REPLACE ? window.scrollY : 0),
      stackSize,
      location: getLatestGeoLocation(),
    };
    const url = args.url ?? (location.pathname + location.search + location.hash);

    if (args.type === PUSH) {
      history.pushState(newState, '', url);
    } else {
      history.replaceState(newState, '', url);
    }
  }

  get state(): HistoryState|undefined {
    return history.state as HistoryState|undefined;
  }
  
  private handleScrollEvent() {
    if (!this.scrollSavingEnabled) {
      return;
    }

    clearTimeout(this.scrollSaveTimer);
    this.scrollSaveTimer = setTimeout(() => {
      this.saveCurrentScroll();
    }, 400) as any as number;
  }

  private saveCurrentScroll() {
    if (!this.state || this.state.scroll !== window.scrollY) {
      // Save the current scroll position.
      this.updateState();
    }
  }
}

export const historyManager = new HistoryManager();